import { useEffect, useRef, useState } from "react";
import { Loader2, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import MusicPicker from "@/components/MusicPicker";

export default function SoundtrackField({ track, volume, voiceId, onChange }) {
  const [mixState, setMixState] = useState(null);
  const mixRef = useRef(null);

  useEffect(() => () => mixRef.current?.pause(), []);

  const playMixPreview = async () => {
    if (mixState === "playing") {
      mixRef.current?.pause();
      setMixState(null);
      return;
    }
    mixRef.current?.pause();
    setMixState("loading");
    try {
      const resp = await api.get("/sound-preview", {
        params: { voice_id: voiceId || "openai:alloy", track: track || "", volume },
        responseType: "blob",
      });
      const a = new Audio(URL.createObjectURL(resp.data));
      a.onended = () => setMixState(null);
      mixRef.current = a;
      await a.play();
      setMixState("playing");
    } catch (err) {
      toast.error(apiError(err));
      setMixState(null);
    }
  };

  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Background music <span className="normal-case tracking-normal text-zinc-600">· optional</span></label>
      <div className="flex gap-2 items-center">
        <div className="flex-1 min-w-0">
          <MusicPicker
            track={track}
            volume={volume}
            testid="create-music"
            onChange={onChange}
          />
        </div>
        <button
          type="button"
          onClick={playMixPreview}
          disabled={mixState === "loading"}
          data-testid="create-mix-preview-btn"
          title="Hear a 10s sample of this voice with your soundtrack"
          className="shrink-0 h-9 px-3 inline-flex items-center gap-1.5 rounded-sm border border-white/10 bg-[#0A0A0A] text-xs font-semibold text-zinc-300 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-60"
        >
          {mixState === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : mixState === "playing" ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          Hear voice + music
        </button>
      </div>
      <p className="text-xs text-zinc-600 mt-1.5">Mixed softly under the narration. Free to preview — no credits used.</p>
    </div>
  );
}
